import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { t as tr } from "../i18n";
import { confirmDialog } from "../dialog";
import { MedalBadge } from "./ProfileStats";

const API = process.env.REACT_APP_API_URL || "";
const TABS = ["wallet", "items", "jokers", "medals", "counters", "stats"];
const TIER_MARKS = ["0", "I", "II", "III", "IV", "V"];

// Inventaire d'un compte, retouchable à la main : pièces, objets, jokers,
// médailles, compteurs, statistiques. Chaque changement part tout de suite au
// serveur, qui le note au journal.
export default function AdminInventory({ userId, authFetch, flash, onClose }) {
  const [inv, setInv] = useState(null);
  const [tab, setTab] = useState("wallet");
  const [query, setQuery] = useState("");
  const [wallet, setWallet] = useState({ coins: 0, junk: 0 });
  const [newCounter, setNewCounter] = useState({ key: "", value: "" });

  // `flash` est recréée à chaque rendu d'Admin : lue par référence, elle ne
  // relance pas le chargement en boucle.
  const flashRef = useRef(flash);
  flashRef.current = flash;
  const load = useCallback(async () => {
    const r = await authFetch(`${API}/admin/users/${userId}/inventory`);
    if (!r?.ok) { flashRef.current(tr("admin.error"), true); return; }
    const d = await r.json();
    setInv(d);
    setWallet(d.wallet);
  }, [authFetch, userId]);
  useEffect(() => { load(); }, [load]);

  const call = async (method, path, body) => {
    const r = await authFetch(`${API}/admin/users/${userId}/${path}`, {
      method, headers: { "Content-Type": "application/json" }, body: body ? JSON.stringify(body) : undefined,
    });
    if (!r?.ok) {
      const d = await r?.json().catch(() => ({}));
      flash(d?.error || tr("admin.error"), true);
      return false;
    }
    await load();
    return true;
  };

  const q = query.trim().toLowerCase();
  const match = (...vals) => !q || vals.some(v => String(v || "").toLowerCase().includes(q));
  const itemsByKind = useMemo(() => {
    const out = new Map();
    for (const it of inv?.items || []) {
      if (!match(it.name, it.kind, it.id)) continue;
      if (!out.has(it.kind)) out.set(it.kind, []);
      out.get(it.kind).push(it);
    }
    return [...out.entries()];
  }, [inv, q]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!inv) return null;
  const { user } = inv;

  return (
    <div className="adm-modal-overlay" onClick={onClose}>
      <div className="adm-modal adm-inv" onClick={e => e.stopPropagation()}>
        <div className="adm-inv-head">
          <h3>{tr("admin.invTitle", { name: user.pseudo || user.username })}</h3>
          <span className="adm-inv-sub">{user.username} · {user.role}</span>
        </div>
        <div className="adm-inv-tabs">
          {TABS.map(k => (
            <button key={k} type="button" className={`adm-pill${tab === k ? " active" : ""}`}
              onClick={() => { setTab(k); setQuery(""); }}>{tr(`admin.invTab_${k}`)}</button>
          ))}
        </div>
        {["items", "medals", "counters"].includes(tab) && (
          <input className="adm-settings-input adm-inv-search" type="search" value={query}
            placeholder={tr("admin.reportSearch")} onChange={e => setQuery(e.target.value)} />
        )}

        <div className="adm-inv-body">
          {tab === "wallet" && (
            <form className="adm-inv-wallet" onSubmit={e => { e.preventDefault(); call("PUT", "wallet", wallet); }}>
              <label>{tr("admin.invCoins")}
                <input className="adm-settings-input" type="number" min="0" value={wallet.coins}
                  onChange={e => setWallet({ ...wallet, coins: e.target.value })} />
              </label>
              <label>{tr("admin.invJunk")}
                <input className="adm-settings-input" type="number" min="0" value={wallet.junk}
                  onChange={e => setWallet({ ...wallet, junk: e.target.value })} />
              </label>
              <button className="btn btn-primary" type="submit">{tr("admin.save")}</button>
            </form>
          )}

          {tab === "items" && itemsByKind.map(([kind, items]) => (
            <div key={kind} className="adm-inv-group">
              <h4>{kind}</h4>
              {items.map(it => (
                <label key={it.id} className="adm-inv-row">
                  <input type="checkbox" checked={it.owned}
                    onChange={() => call(it.owned ? "DELETE" : "POST", `items/${encodeURIComponent(it.id)}`)} />
                  <span className="adm-inv-name">{it.name}</span>
                  <span className="adm-inv-meta">
                    {it.rarity}{it.hidden ? ` · ${tr("admin.invHidden")}` : ""}{it.equipped ? ` · ${tr("admin.invEquipped")}` : ""}
                  </span>
                </label>
              ))}
            </div>
          ))}

          {tab === "jokers" && inv.jokers.map(j => (
            <div key={j.id} className="adm-inv-row">
              <span className="adm-inv-name">{j.name}</span>
              <input className="adm-settings-input adm-inv-num" type="number" min="0" defaultValue={j.qty}
                key={`${j.id}-${j.qty}`}
                onBlur={e => { if (Number(e.target.value) !== j.qty) call("PUT", `jokers/${encodeURIComponent(j.id)}`, { qty: e.target.value }); }} />
            </div>
          ))}

          {tab === "medals" && (<>
            <p className="adm-inv-hint">{tr("admin.invMedalsHint")}</p>
            {inv.medals.filter(m => match(m.name, m.family, m.id)).map(m => (
              <div key={m.id} className="adm-inv-row">
                <MedalBadge medal={{ ...m, unlocked: m.tier >= 0 }} size={30} />
                <span className="adm-inv-name">{m.name}</span>
                <select className="adm-param-select adm-inv-tier" value={m.tier}
                  onChange={e => {
                    const tier = Number(e.target.value);
                    call(tier < 0 ? "DELETE" : "PUT", `medals/${encodeURIComponent(m.id)}`, tier < 0 ? null : { tier });
                  }}>
                  <option value={-1}>{tr("admin.invNotEngraved")}</option>
                  {Array.from({ length: m.tierCount }, (_, i) => (
                    <option key={i} value={i}>{m.tierCount > 1 ? tr("medals.toastTier", { tier: TIER_MARKS[i] }) : tr("admin.invEngraved")}</option>
                  ))}
                </select>
              </div>
            ))}
          </>)}

          {tab === "counters" && (<>
            <p className="adm-inv-hint">{tr("admin.invCountersHint")}</p>
            {Object.entries(inv.counters).filter(([k]) => match(k)).sort(([a], [b]) => a.localeCompare(b)).map(([k, v]) => (
              <div key={k} className="adm-inv-row">
                <code className="adm-inv-name">{k}</code>
                <input className="adm-settings-input adm-inv-num" type="number" min="0" defaultValue={v} key={`${k}-${v}`}
                  onBlur={e => { if (Number(e.target.value) !== v) call("PUT", `counters/${encodeURIComponent(k)}`, { value: e.target.value }); }} />
              </div>
            ))}
            <form className="adm-inv-row adm-inv-add" onSubmit={async e => {
              e.preventDefault();
              if (!newCounter.key.trim()) return;
              if (await call("PUT", `counters/${encodeURIComponent(newCounter.key.trim())}`, { value: newCounter.value })) setNewCounter({ key: "", value: "" });
            }}>
              <input className="adm-settings-input" placeholder={tr("admin.invCounterName")} value={newCounter.key}
                onChange={e => setNewCounter({ ...newCounter, key: e.target.value })} />
              <input className="adm-settings-input adm-inv-num" type="number" min="0" placeholder="0" value={newCounter.value}
                onChange={e => setNewCounter({ ...newCounter, value: e.target.value })} />
              <button className="btn btn-primary" type="submit">{tr("admin.add")}</button>
            </form>
          </>)}

          {tab === "stats" && (
            <div className="adm-inv-stats">
              <dl>
                <dt>{tr("admin.invGames")}</dt><dd>{inv.stats.games}</dd>
                <dt>{tr("admin.invWins")}</dt><dd>{inv.stats.wins}</dd>
                <dt>{tr("admin.invCorrect")}</dt><dd>{inv.stats.correct} / {inv.stats.total}</dd>
              </dl>
              <button type="button" className="btn adm-inv-danger" onClick={async () => {
                if (await confirmDialog(tr("admin.invConfirmReset", { name: user.pseudo || user.username }), { danger: true })) {
                  if (await call("POST", "stats/reset")) flash(tr("admin.invResetDone"));
                }
              }}>{tr("admin.invResetStats")}</button>
            </div>
          )}
        </div>

        <div className="adm-modal-actions">
          <button className="btn" onClick={onClose}>{tr("admin.close")}</button>
        </div>
      </div>
    </div>
  );
}
